import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Route, useLocation, useHistory } from 'react-router-dom';
import { usePrevious } from './common/hooks';
import { subscribeMapReady } from './interface';
import Mapbox from './Map/Map';

import { ClipLoader } from 'react-spinners';

const NotFound = lazy(() => import('./Pages/ErrorPages/404.react'));
const Sidebar = lazy(() => import('./Sidebar/Sidebar'));
const LocationEditor = lazy(() => import('./Place/PlaceEditor/PlaceEditor'));
const LocationWrapper = lazy(() => import('./Place/PlaceWrapper'));
const ModelUpload = lazy(() =>
  import('./ModelLibrary/ModelUpload/ModelUpload')
);
const ModelLibrary = lazy(() => import('./ModelLibrary/ModelLibrary'));
const Model = lazy(() => import('./ModelLibrary/Model/Model'));
const Settings = lazy(() => import('./Settings/Settings'));
const Lights = lazy(() => import('./Light/Lights'));
const LightEditor = lazy(() => import('./Light/LightEditor/LightEditor'));

const MapReadyGate = ({ children }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return undefined;
    return subscribeMapReady(() => setReady(true));
  }, [ready]);

  if (!ready) {
    return <ClipLoader />;
  }

  return children;
};

const Routes = () => {
  const location = useLocation();
  const history = useHistory();
  const lastLocation = usePrevious(location);

  useEffect(() => {
    // Keycloak adds state/session_state fragments we should ignore
    const hash = window.location.hash.slice(1);
    if (!hash) {
      return;
    }

    const hasKeycloakParams =
      hash.startsWith('/state=') ||
      hash.includes('session_state=') ||
      hash.includes('iss=') ||
      hash.includes('code=');
    if (hasKeycloakParams) {
      if (location.pathname !== '/') {
        history.replace({
          pathname: '/',
          search: window.location.search,
        });
      }
      return;
    }

    if (!hash.startsWith('/')) {
      return;
    }

    const [path, ...rest] = hash.split('?');
    const hashQuery = rest.length ? `?${rest.join('?')}` : '';
    if (path === `/${location.search}`) {
      history.replace({
        pathname: '/',
        search: hashQuery || location.search,
      });
      return;
    }

    if (location.pathname !== path) {
      history.replace({
        pathname: path,
        search: hashQuery || location.search,
      });
    }
  }, [history, location.pathname, location.search]);
  return (
    <div>
      <Route path="/">
        <Suspense fallback={<ClipLoader />}>
          <Sidebar>
            <Suspense fallback={<ClipLoader />}>
              <MapReadyGate>
                <Route exact path={'/places'} component={LocationWrapper} />
                <Route
                  exact
                  path={'/places/model/:id'}
                  component={LocationWrapper}
                />
                <Route
                  path="/new-place"
                  render={() => <LocationEditor lastLocation={lastLocation} />}
                />
                <Route
                  path="/place/:id"
                  render={() => <LocationEditor lastLocation={lastLocation} />}
                />
                <Route path="/models" component={ModelLibrary} />
                <Route
                  path="/model/:id"
                  render={() => <Model lastLocation={lastLocation} />}
                />
                <Route
                  exact
                  path="/upload"
                  render={() => <ModelUpload lastLocation={lastLocation} />}
                />
                <Route exact path="/upload/:id" component={ModelUpload} />
                <Route
                  path="/settings"
                  render={() => <Settings currentSettings={{ minZoom: 17 }} />}
                />
                <Route path="/lights" component={Lights} />
                <Route exact path="/light" component={LightEditor} />
                <Route exact path="/light/:id" component={LightEditor} />
              </MapReadyGate>
            </Suspense>
          </Sidebar>
        </Suspense>
      </Route>
      <Route path="/" component={Mapbox} />
      <Suspense fallback={<ClipLoader />}>
        <Route exact path="/notfound" component={NotFound} />
      </Suspense>
    </div>
  );
};

export default Routes;
